/**
 * Wire protocol constants.
 *
 * The JSON WebSocket protocol uses dotted string identifiers (e.g.
 * `"records.query"`) as the `type` discriminator. All message types are
 * grouped under a single `MSG` object so imports stay tidy:
 *
 *     import { MSG, dispatch, clientBuild } from 'deepspace'
 *
 *     dispatch<ServerMessage>(raw, {
 *     })
 *
 * Each key's value is the on-wire string — grep-friendly, self-
 * documenting, and infinite per namespace. Adding a new message is one
 * line here plus one arm in the discriminated union in `./messages.ts`.
 *
 * Yjs binary protocol constants (`MSG_YJS_SYNC`, `MSG_YJS_AWARENESS`) are
 * intentionally kept numeric and separate from `MSG` — they ride a
 * binary WebSocket frame format and aren't part of the JSON dispatcher.
 */

export const MSG = {
  // ---- Records / CRUD --------------------------------------------------
  SUBSCRIBE: 'core.subscribe',
  UNSUBSCRIBE: 'core.unsubscribe',
  QUERY_RESULT: 'core.query_result',
  RECORD_CHANGE: 'core.record_change',
  PUT: 'core.put',
  DELETE: 'core.delete',
  ERROR: 'core.error',

  // ---- Users -----------------------------------------------------------
  USER_INFO: 'user.info',
  USER_LIST: 'user.list',
  SET_ROLE: 'user.set_role',
  USER_UPDATE: 'user.update',

  // ---- Auth signal -----------------------------------------------------
  // Server → client one-shot on connect from rooms that enforce role-based
  // writes (Canvas, Cron). Tells the client whether this connection
  // can write so its hook can disable local write APIs and the UI can
  // render controls accordingly. Without this, a viewer's click would
  // round-trip to the server and silently come back as ERROR.
  AUTH: 'auth',

  // ---- Yjs JSON control frames (binary sync lives in ./yjs.ts) ---------
  YJS_JOIN: 'yjs.join',
  YJS_LEAVE: 'yjs.leave',

  // ---- Records room misc -----------------------------------------------
  ACK: 'records.ack',
  LIST_SCHEMAS: 'records.list_schemas',
  RESUBSCRIBE: 'records.resubscribe',

  // ---- CanvasRoom ------------------------------------------------------
  CANVAS_SHAPES: 'canvas.shapes',
  CANVAS_ADD: 'canvas.add',
  CANVAS_MOVE: 'canvas.move',
  CANVAS_RESIZE: 'canvas.resize',
  CANVAS_DELETE: 'canvas.delete',
  CANVAS_UPDATE: 'canvas.update',
  CANVAS_VIEWPORT: 'canvas.viewport',
  CANVAS_UNDO: 'canvas.undo',
  CANVAS_REDO: 'canvas.redo',

  // ---- CronRoom --------------------------------------------------------
  CRON_TASKS: 'cron.tasks',
  CRON_HISTORY: 'cron.history',
  CRON_TRIGGER: 'cron.trigger',
  CRON_PAUSE: 'cron.pause',
  CRON_RESUME: 'cron.resume',
  CRON_STATUS: 'cron.status',
  // Per-mutation receipt, sent only to the socket whose trigger/pause/
  // resume carried a `requestId` — frames without one stay fire-and-forget.
  CRON_ACK: 'cron.ack',

  // ---- JobRoom ---------------------------------------------------------
  // Background-job queue with durable, alarm-driven execution. See
  // server/rooms/job-room.ts. Clients enqueue and subscribe to updates;
  // the room broadcasts JOB_UPDATE for every status transition.
  JOB_ENQUEUE: 'job.enqueue',
  JOB_CANCEL: 'job.cancel',
  JOB_RETRY: 'job.retry',
  JOB_UPDATE: 'job.update',

  // ---- PresenceRoom ----------------------------------------------------
  PRESENCE_SYNC: 'presence.sync',
  PRESENCE_JOIN: 'presence.join',
  PRESENCE_LEAVE: 'presence.leave',
  PRESENCE_UPDATE: 'presence.update',
} as const

/**
 * Type of any message type constant — the union of every string literal
 * stored in `MSG`. Useful when declaring functions that accept "any known
 * message type" without enumerating all 54 strings by hand.
 */
export type MsgType = (typeof MSG)[keyof typeof MSG]

// ---------------------------------------------------------------------------
// Yjs binary protocol (NOT part of MSG — see header comment)
// ---------------------------------------------------------------------------

/** Outer envelope id for binary-framed yjs sync messages. Varuint-encoded. */
export const MSG_YJS_SYNC = 22
/** Outer envelope id for binary-framed yjs awareness messages. Varuint-encoded. */
export const MSG_YJS_AWARENESS = 23

// ---------------------------------------------------------------------------
// Role names — not wire messages, just the string values the auth layer uses
// as role identifiers.
// ---------------------------------------------------------------------------

/** Role assigned to unauthenticated WebSocket connections */
export const ROLE_ANONYMOUS = 'viewer'
/** Prefix `base-room` mints anonymous socket ids with. */
export const ANONYMOUS_USER_ID_PREFIX = 'anon-'
/** The one test for an anonymous socket identity — `ROLE_ANONYMOUS` cannot be
 *  it, since authenticated users can legitimately hold the same role name. */
export function isAnonymousUserId(userId: string): boolean {
  return userId.startsWith(ANONYMOUS_USER_ID_PREFIX)
}
/** Default role for newly registered authenticated users */
export const ROLE_DEFAULT = 'member'
/** Admin role */
export const ROLE_ADMIN = 'admin'

// ---------------------------------------------------------------------------
// Error strings that cross a room boundary and are matched by the caller
// ---------------------------------------------------------------------------

/**
 * Reported when a record id resolves to nothing.
 *
 * This crosses the DO boundary as a response body and callers branch on it,
 * so it is a contract rather than prose. `getChat` in particular treats it as
 * "no such chat" and returns null — the same answer it gives for a chat owned
 * by someone else, which is what stops chat ids being enumerable. If a room
 * reworded this independently, a miss would start throwing while a cross-user
 * hit kept returning null, and that difference is an existence oracle.
 */
export const RECORD_NOT_FOUND = 'Record not found'

/** How long RecordRoom retains a confirmed write outcome. */
export const CONFIRMED_WRITE_RETENTION_MS = 24 * 60 * 60 * 1000
/** Stop resending before the server may prune the outcome; reserve a minute for transit. */
export const CONFIRMED_WRITE_RETRY_MS = CONFIRMED_WRITE_RETENTION_MS - 60_000
